import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import RelativeTime from '@99designs/react-components/components/RelativeTime';
import Avatar from '@99designs/react-components/components/Avatar';
import DisplayName from '@99designs/react-components/components/DisplayName';
import ButtonList, { ButtonListItem } from '@99designs/react-components/components/ButtonList';
import IconButton from '@99designs/react-components/components/IconButton';
import Icon from '@99designs/react-components/components/Icon';
import Alert from '@99designs/react-components/components/Alert';

import propertyOrNull from '@99designs/utils/app/propertyOrNull';
import CommentForm from './CommentForm';
import Transition from 'react-transition-group/Transition';

// Actions
import {
    selectDesignComment,
    createDesignComment,
    updateDesignComment,
    editDesignComment,
    cancelEditDesignComment,
    deleteDesignComment,
    cancelEnterDesignComment,
} from '../actions/design-comments';

export default class Comment extends React.Component {
    constructor(props) {
        super(props);
        this.deleteActionTimeout = null;
        this.editActionTimeout = null;
        this.state = {
            isEditActionBlocked: false,
            isDeleteActionBlocked: false,
            isVisible: true,
            transition: '',
        };
        this.handleClick = this.handleClick.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.handleEditClick = this.handleEditClick.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
    }

    componentWillUnmount() {
        window.clearTimeout(this.deleteActionTimeout);
        window.clearTimeout(this.editActionTimeout);
    }

    componentWillMount() {
        this.setActionExpiry(this.props.comment);
    }
    componentWillReceiveProps(nextProps) {
        if (nextProps.status === 'error') {
            this.setState({ isVisible: false });
        }
        if (
            nextProps.comment.permission.secondsRemainingEdit !==
            this.props.comment.permission.secondsRemainingEdit
        ) {
            this.setActionExpiry(nextProps.comment);
        }

        if (parseInt(this.props.comment.permission.secondsRemainingDelete, 10) > 0) {
            if (
                nextProps.comment.permission.secondsRemainingDelete !==
                this.props.comment.permission.secondsRemainingDelete
            ) {
                this.setActionExpiry(nextProps.comment);
            }
        }
    }

    render() {
        var comment = this.props.comment;
        var classes = classnames({
            comment: true,
            'comment--comment-pane': this.props.commentLocation === 'pane',
            'comment--spacious': this.props.hasExtraPadding,
            'comment--highlight': this.isHighlighted(),
            'comment--sending': this.props.status === 'saving',
            'comment--deleting': this.props.status === 'deleting',
            'comment--error': this.props.status === 'error',
            'comment--annotation': comment.annotation && this.props.canShowAnnotationMarker,
            'comment--deleted': comment.isDeleted,
            [`comment--transition-${this.state.transition}`]: !!this.state.transition,
        });
        return (
            <Transition
                in={this.state.isVisible}
                timeout={{ enter: 0, exit: 500 }}
                onExiting={() => this.setState({ transition: 'exited' })}
                onExited={() => this.handleDelete()}
            >
                <div className={classes} onClick={this.handleClick}>
                    {this.visibleHeader()}
                    {this.deletedAlert()}
                    <div className="comment__body">
                        {this.getCommentBodyElement(this.props.comment, this.props.status)}
                    </div>
                    {this.commentActions()}
                </div>
            </Transition>
        );
    }

    // Handles cases where the permission to complete an action expires after render
    setActionExpiry(comment) {
        const newState = {};

        ['Edit', 'Delete'].forEach(name => {
            // State is temporary, props are the source of truth.
            // If the props say permission is denied,
            // there's no need for our state and timers
            if (
                !propertyOrNull(comment.permissions, `can${name}`) &&
                this.state[`is${name}ActionBlocked`]
            ) {
                newState[`is${name}ActionBlocked`] = false;
                window.clearTimeout(this[`${name.toLowerCase()}ActionTimeout`]);
            }

            // If the incoming comment has an expiry, set a timer to modify the state
            if (parseInt(propertyOrNull(comment.permission, `secondsRemaining${name}`), 10) > 0) {
                this[`${name.toLowerCase()}ActionTimeout`] = window.setTimeout(() => {
                    // this syntax is due to a limitation in es6 template literals
                    const state = {};
                    state[`is${name}ActionBlocked`] = true;
                    this.setState(state);
                }, comment.permission[`secondsRemaining${name}`] * 1000);
            }
        });

        // single combined state change for all actions to avoid re-renders
        this.setState(newState);
    }

    handleClick(e) {
        if (!this.props.isSelected) {
            this.context.executeAction(selectDesignComment, { comment: this.props.comment });
        }
    }

    handleSubmit(message) {
        const comment = Object.assign({}, this.props.comment, { message, creator: this.getUser() });

        if (!!this.props.comment.id) {
            this.context.executeAction(updateDesignComment, {
                comment,
                csrfToken: this.context.csrfToken,
            });
        } else {
            this.context.executeAction(createDesignComment, {
                comment,
                design: this.props.design,
                csrfToken: this.context.csrfToken,
            });
        }
    }

    handleCancel() {
        if (!!this.props.comment.id) {
            this.context.executeAction(cancelEditDesignComment, { comment: this.props.comment });
        } else {
            this.context.executeAction(cancelEnterDesignComment, { comment: this.props.comment });
        }
    }
    handleDelete() {
        this.context.executeAction(deleteDesignComment, {
            comment: this.props.comment,
            csrfToken: this.context.csrfToken,
        });
    }
    handleDeleteClick() {
        this.setState({ isVisible: false });
    }

    handleEditClick() {
        this.context.executeAction(editDesignComment, {
            comment: this.props.comment,
            editingLocation: this.props.commentLocation,
        });
    }

    getUser() {
        return this.context.activeUser;
    }

    getCommentForm(comment) {
        return (
            <CommentForm
                onSubmit={this.handleSubmit}
                onCancel={this.handleCancel}
                hasCancelButton={true}
                isAutoFocus={true}
                isVertical={true}
                comment={comment}
                criticalError={this.timeoutErrorMessage()}
            />
        );
    }

    timeoutErrorMessage() {
        if (
            (this.hasEditActionTimedOut() || this.haveAllActionsTimedOut()) &&
            this.props.status === 'editing'
        ) {
            return <FormattedMessage id="comment_action_timeout" />;
        }
    }

    getCommentContent(comment, status) {
        if (status === 'saving' || status === 'error') {
            return comment.message.split('\n').map(function(item, i) {
                return <p key={`unsaved-comment#{i}`}>{item}</p>;
            });
        } else {
            return (
                <div
                    dangerouslySetInnerHTML={{ __html: comment.formattedMessage }}
                    key={comment.id}
                />
            );
        }
    }

    getCommentStatusElement(comment, status) {
        if (status === 'error') {
            return <FormattedMessage id="comment_error" />;
        } else if (status === 'deleting') {
            return <FormattedMessage id="comment_deleting" />;
        } else if (status === 'saving') {
            return <FormattedMessage id="comment_sending" />;
        } else {
            return <RelativeTime date={comment.createdAt} />;
        }
    }

    getCommentBodyElement(comment, status) {
        if (status === 'editing' || status === 'creating') {
            return this.getCommentForm(comment);
        } else {
            return this.getCommentContent(comment, status);
        }
    }

    hasEditActionTimedOut() {
        return this.state.isEditActionBlocked;
    }

    haveAllActionsTimedOut() {
        return this.state.isEditActionBlocked && this.state.isDeleteActionBlocked;
    }

    isHighlighted() {
        return this.props.isSelected && this.props.canHighlight;
    }

    commentActions() {
        const actions = [];
        const permissions = this.props.comment.permission;

        // there are a few scenarios where we skip actions.
        // Exit early if we meet any of those to simplify later conditionals
        if (!this.props.isSelected || (!permissions.canDelete && !permissions.canEdit)) {
            return null;
        }

        if (permissions.canEdit && !this.state.isEditActionBlocked) {
            actions.push(
                <ButtonListItem key="editButton">
                    <IconButton
                        variations={this.isHighlighted() ? [] : ['on-white']}
                        onClick={this.handleEditClick}
                    >
                        <Icon variations={['edit-1']} />
                    </IconButton>
                </ButtonListItem>
            );
        }

        if (permissions.canDelete && !this.state.isDeleteActionBlocked) {
            actions.push(
                <ButtonListItem key="deleteButton">
                    <IconButton
                        variations={this.isHighlighted() ? [] : ['on-white']}
                        onClick={this.handleDeleteClick}
                    >
                        <Icon variations={['trash-2']} />
                    </IconButton>
                </ButtonListItem>
            );
        }

        if (actions.length === 0) {
            return null;
        }

        return (
            <div className="comment__footer">
                <ButtonList variations={['spacious']} children={actions} />
            </div>
        );
    }

    visibleHeader() {
        if (this.props.isHeaderVisible) {
            return (
                <div className="comment__header">
                    <div className="comment__header__avatar">
                        <Avatar
                            user={this.props.comment.creator}
                            variations={['block', 'extra-small']}
                        />
                    </div>
                    <div className="comment__header__user">
                        <DisplayName user={this.props.comment.creator} />
                    </div>
                    <div className="comment__header__status">
                        {this.getCommentStatusElement(this.props.comment, this.props.status)}
                    </div>
                </div>
            );
        }
    }

    deletedAlert() {
        if (this.props.comment.isDeleted) {
            return (
                <Alert>
                    <FormattedMessage id="comment_deleted" />
                </Alert>
            );
        }
    }
}

Comment.contextTypes = {
    executeAction: PropTypes.func,
    activeUser: PropTypes.object,
    csrfToken: PropTypes.string,
};

Comment.propTypes = {
    design: PropTypes.object.isRequired,
    canHighlight: PropTypes.bool,
    canShowAnnotationMarker: PropTypes.bool,
    comment: PropTypes.object,
    hasExtraPadding: PropTypes.bool,
    isHeaderVisible: PropTypes.bool,
    isSelected: PropTypes.bool,
    status: PropTypes.oneOf(['creating', 'editing', 'saving', 'deleting', 'error']),
    commentLocation: PropTypes.oneOf(['pane', 'canvas']),
};

Comment.defaultProps = {
    hasExtraPadding: false,
    isSelected: false,
    isHeaderVisible: true,
    canHighlight: true,
    canShowAnnotationMarker: true,
    commentLocation: 'pane',
};



// WEBPACK FOOTER //
// ./~/@99designs/ddo/components/Comment.jsx